import type { ThunkAction, ThunkActionDispatch } from 'redux-thunk';
import { createDraftPost, updateDraftPost } from '@wix/ambassador-blog-v3-draft/http';
import {
  type BlogMedia,
  type DraftPost,
  DraftPostSaveTypeType,
  type EmbedMedia,
  Field,
  type WixMedia,
} from '@wix/ambassador-blog-v3-draft/types';
import {
  createAction,
  extractHashtagsFromContent,
  isValidUrl,
  validateDraftFieldNotEmpty,
} from '@wix/communities-blog-client-common';
import { getCurrentUser } from '@app/external/common/store/auth/current-user-selectors';
import { type PostPageThunkAction } from '@app/external/post-page/types';
import { createPromisifiedAction } from '../actions-promisifier/create-promisified-action';
import createPermissionChecker from '../services/create-permission-checker';
import createPermissionHelpers from '../services/create-permission-helpers';
import { discardDeletedCategoryIds, normalizePost } from '../services/post-utils';
import { getCategoryIds } from '../store/categories/categories-selectors';
import { setIsLoading } from '../store/is-loading/is-loading-actions';

import { hideLoading, showLoading } from '../store/loading-bar/loading-bar-actions';
import { updatePostEditorPost } from '../store/post-editor/post-editor-actions';
import type { AppState, NormalizedPost } from '../types';
import {
  SAVE_POST_DRAFT_FAILURE,
  SAVE_POST_DRAFT_REQUEST,
  SAVE_POST_DRAFT_SUCCESS,
  STOP_LOADING_DEBOUNCED,
} from './save-post-draft-types';

const savePostDraftRequest = createAction(SAVE_POST_DRAFT_REQUEST);
const savePostDraftSuccess = createAction(SAVE_POST_DRAFT_SUCCESS);
const savePostDraftFailure = createAction(SAVE_POST_DRAFT_FAILURE);

const TIMEOUT = 1000;
const ENTITY = 'postDraft';
const stopLoading =
  ({ draftKey }: { draftKey: string }): ThunkAction<void, AppState, unknown, any> =>
  (dispatch) =>
    dispatch(setIsLoading(ENTITY, draftKey, false));

const stopLoadingDebounced =
  ({ draftKey }: { draftKey: string }): ThunkAction<void, AppState, unknown, any> =>
  (dispatch) => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const action = (dispatch: ThunkActionDispatch<any>) => {
      dispatch(stopLoading({ draftKey }));
      dispatch(hideLoading());
    };

    action.type = STOP_LOADING_DEBOUNCED;
    action.meta = { debounce: TIMEOUT };

    return dispatch(action);
  };

type SavePostDraftParams = {
  id?: string;
  title?: string;
  canonicalUrl?: string;
  content?: Record<string, any>;
  excerpt?: string;
  categoryIds: string[];
  coverImage?: LegacyWixMediaImage | LegacyEmbedMediaThumbnail;
  firstPublishedDate?: string;
  seoSlug?: string;
  seoTitle?: string;
  seoDescription?: string;
  isFeatured?: boolean;
  ownerSiteMemberId?: string;
  draftKey: string;
  isManualSave?: boolean;
};

export const savePostDraft =
  ({
    id,
    title,
    canonicalUrl,
    content,
    excerpt,
    categoryIds = [],
    coverImage,
    firstPublishedDate,
    seoSlug,
    seoTitle,
    seoDescription,
    isFeatured,
    ownerSiteMemberId,
    draftKey = '',
    isManualSave,
  }: SavePostDraftParams): PostPageThunkAction<NormalizedPost | void> =>
  async (dispatch, getState, { httpClient, errorHandler }) => {
    const state = getState();
    dispatch(savePostDraftRequest({ draftKey }));
    dispatch(showLoading());
    const { withErrorHandler, getResolvedError } = errorHandler;
    const { can } = createPermissionHelpers(createPermissionChecker(state, getCurrentUser(state)));

    const isCreated = Boolean(id);

    /**
     * Remove metadata from rich content object
     * since it's not needed and causes API errors
     */
    if (content && 'metadata' in content) {
      delete content.metadata;
    }

    const fieldsets = [Field.RICH_CONTENT, Field.TRANSLATIONS];

    const draftPost: DraftPost = {
      id: draftKey,
      title: validateDraftFieldNotEmpty(title, 'Untitled'),
      richContent: content,
      excerpt: validateDraftFieldNotEmpty(excerpt, isCreated ? null : undefined),
      categoryIds: discardDeletedCategoryIds(categoryIds, getCategoryIds(state)),
      media: toMedia(coverImage),
      hashtags: extractHashtagsFromContent(content),
      firstPublishedDate: firstPublishedDate ? new Date(firstPublishedDate) : undefined,
      seoSlug: validateDraftFieldNotEmpty(seoSlug),
      seoData: {
        tags: [
          {
            type: 'title',
            children: seoTitle,
          },
          {
            type: 'meta',
            props: {
              name: 'description',
              content: seoDescription,
            },
          },
        ],
      },
      featured: can('toggle-featured', 'post') ? isFeatured : undefined,
      memberId: can('change-owner', 'post') ? ownerSiteMemberId : undefined,
    };

    const saveType = isManualSave ? DraftPostSaveTypeType.MANUAL : DraftPostSaveTypeType.AUTO_SAVE;

    const updateOrSaveDraftPostPromise = withErrorHandler(
      () =>
        httpClient
          .request(
            isCreated
              ? updateDraftPost({
                  draftPost,
                  fieldsets,
                  saveType,
                })
              : createDraftPost({
                  draftPost,
                  fieldsets,
                  saveType,
                }),
          )
          .then(({ data }) => {
            if (!data.draftPost) {
              throw new Error('Draft post not found');
            }

            const normalizedPost = normalizePost({
              post: data.draftPost,
              blogCategoryIds: getCategoryIds(getState()),
            });

            dispatch(savePostDraftSuccess({ post: normalizedPost, draftKey }));
            dispatch(updatePostEditorPost(normalizedPost));
            return normalizedPost;
          })
          .catch((error) => {
            getResolvedError(error);
            dispatch(savePostDraftFailure(error));
          })
          .finally(() => {
            dispatch(stopLoadingDebounced({ draftKey }));
          }),
      { errorCodesMap: {} },
    );

    return updateOrSaveDraftPostPromise;
  };

export const savePostDraftPromisified = createPromisifiedAction(
  savePostDraft,
  (data) => data,
  (response) => response.status,
);

function toMedia(image?: LegacyWixMediaImage | LegacyEmbedMediaThumbnail): BlogMedia {
  const custom = image?.isCustom ?? false;
  const displayed = image?.isEnabled ?? true;
  const embedMedia = isLegacyEmbedMediaThumbnail(image)
    ? {
        thumbnail: toEmbedMediaThumbnail(image),
      }
    : undefined;

  const wixMedia = !isLegacyEmbedMediaThumbnail(image)
    ? { image: toWixMediaImage(image) }
    : undefined;

  return {
    custom,
    displayed,
    wixMedia,
    embedMedia,
  };
}

const toWixMediaImage = (image: LegacyWixMediaImage | undefined): WixMedia['image'] => {
  if (image?.src) {
    return {
      id: image?.src?.id ?? image?.src?.file_name ?? image?.imageMetadata?.file_name,
      width: image.src.width,
      height: image.src.height,
    };
  }

  return undefined;
};

const toEmbedMediaThumbnail = (image: LegacyEmbedMediaThumbnail): EmbedMedia['thumbnail'] => {
  if (image?.src) {
    return {
      url: image.src,
      width: image.videoMetadata?.thumbnail_width,
      height: image.videoMetadata?.thumbnail_height,
    };
  }
};

type LegacyWixMediaImage = {
  src?: {
    id?: string;
    file_name?: string;
    width: number;
    height: number;
    original_file_name?: string;
    url?: string;
  };
  imageMetadata?: {
    file_name?: string;
  };
  isEnabled: boolean;
  isCustom: boolean;
};

type LegacyEmbedMediaThumbnail = {
  src?: string;
  videoMetadata?: {
    thumbnail_height?: number;
    thumbnail_width?: number;
    thumbnail_url?: string;
    video_url?: string;
  };
  isCustom?: boolean;
  isEnabled?: boolean;
  isResolved?: boolean;
  shouldRender?: boolean;
};

export const isLegacyEmbedMediaThumbnail = (
  value: LegacyWixMediaImage | LegacyEmbedMediaThumbnail | undefined,
): value is LegacyEmbedMediaThumbnail => {
  return typeof value?.src === 'string' && isValidUrl(value.src);
};
